// THE ORION ROLL ORDERING (CR-20260904-001 follow-on, 2026-09-07).
//
// What a roll pays, divided by the days it ties the position up. The owner's rule, in his words:
// the trader is renting out risk by the day and intends to keep rolling, so the question is not
// which date pays the MOST but which pays the best RATE for the time it costs. A large credit far
// out is bought with weeks of exposure that cannot be used - "otherwise we just roll once a year and
// be done with it".
//
// ⚠ ONE SIGNED NUMBER, ONE DESCENDING SORT - there is NO debit rule. Owner, O48: "there is no
// concept of the different treatment of them. The sorting must happen in descending order, from best
// paying to worst paying, which will naturally place the debit-paying instead of the credit-paying
// down to the bottom. If there is no credit-paying, the debit adjustments will also be sorted from
// least debt to most debt." Both behaviours fall out of the arithmetic. **Any `if (debit)` branch
// added here is the regression**, not the fix.
//
// Pure / Angular-free so the rule is unit-tested directly rather than through the skill.
// ES5 note: arrays + indexOf only (no Set / for..of), matching the rest of the copilot logic.

// ⚠ WHAT LEFT THIS FILE ON 2026-09-10 (CR-20260910-001 milestone 1, contract A1 §4.6):
// `perDayCredit(price, daysOut)` - the RATE itself. The backend computes it now and delivers it on
// the card as `PerDayCredit` (null when the date has no days left to run, which is the same O49
// answer expressed on the wire).
//
// WHAT STAYED, AND WHY THE FILE IS NOT DELETED: the whole SORT-KEY machinery below. The trader
// re-sorts the delivered list in the browser (O47: "the user can manually re-click that sorting
// label, which will re-sort"), so the comparator has to be here. It sorts on the delivered
// `perDayCredit` - the exact number the run ranked on - rather than re-deriving one from the
// rounded money on the card, so there is still exactly ONE definition of "better" on this side.
//
// ⚠ THE BACKEND HOLDS THE MATCHING COMPARATOR (type A->Z, then per-day credit, shorter expiration
// breaking an exact tie, incoming order last). Two languages, one rule: if this one is edited, A1
// §4.7 is edited with it, or a manual re-sort will disagree with the order the run delivered.

/**
 * The one label a lane sorts under. A couple carried on one Price Box row is ONE lane (owner ruling
 * O50a), so its two types make one label - the same string the trader reads on the card.
 */
export function typeLabelOf(adjustmentTypes: string[] | null | undefined): string {
    const types = adjustmentTypes || [];
    return types.join(' ');
}

// ---- the sort keys ---------------------------------------------------------------------------
//
// Mirrors the Hedge Matrix results toolbar, which is what the owner asked for by name: "a little
// label to sort the results by price, similar to the concept we have in the Hedge Matrix ORION".
// Two keys, one direction each; 'none' takes a key OUT of the comparison entirely - it does not mean
// "ascending by default".

export type OrionSortDirection = 'desc' | 'asc' | 'none';

export interface OrionSortKeys {
    /** Adjustment type, ALPHABETICAL (owner ruling O57). 'asc' = A->Z. */
    type: OrionSortDirection;
    /** Per-day credit. 'desc' = best paying first, credits above debits. */
    price: OrionSortDirection;
}

/**
 * What a fresh result set is ordered by: **grouped by adjustment type A->Z, best per-day credit
 * first inside each type.**
 *
 * BOTH KEYS ARE ON BY DEFAULT (owner ruling, 2026-09-07: "default Type sort order is Ascending").
 * Type is the OUTER key, so the top of the list is the best-paying roll OF THE FIRST TYPE
 * ALPHABETICALLY - not the best-paying roll overall. That is the point: with lane blocks ruled out,
 * the Type key is how he chooses which adjustment types lead, and he wanted them leading from the
 * start rather than after a click.
 *
 * His earlier "by default, it should be sorted from the best price to the worst" still holds and is
 * not in tension with this - it governs the PRICE key's direction, which is why `price` is `desc`.
 */
export const DEFAULT_ORION_SORT_KEYS: OrionSortKeys = { type: 'asc', price: 'desc' };

/**
 * One click on a key. Three states, and the FIRST click always gives that key's natural reading:
 * price starts at 'desc' (best first), type at 'asc' (A->Z). After that both cycle through the
 * reverse and then out.
 *
 * ⚠ `[my call]`, flagged for the smoke - the Hedge Matrix cycles every key desc -> asc -> none,
 * because there 'desc' IS the natural reading for both of its keys. Here it is not: a first click on
 * Type that answered Z->A would read as a bug. One line to change if the owner wants strict parity.
 */
export function nextSortDirection(key: 'type' | 'price', current: OrionSortDirection): OrionSortDirection {
    if (key === 'price') {
        return current === 'desc' ? 'asc' : (current === 'asc' ? 'none' : 'desc');
    }
    return current === 'asc' ? 'desc' : (current === 'desc' ? 'none' : 'asc');
}

// ---- the ordering ----------------------------------------------------------------------------
//
// THE ORDER, top to bottom:
//   0. ADJUSTMENT TYPE, when the key is on - alphabetical (reversed on 'desc');
//   1. PER-DAY CREDIT, best first (reversed on 'asc');
//   2. the SHORTER expiration - the CR's own tie-break, "where two are exactly tied, the shorter
//      expiration ranks first";
//   3. the incoming order, as the final stable tie-break.
//
// ⚠ RULE 2 IS NOT REVERSED BY 'asc'. It settles two cells paying the same rate; flipping it on the
// ascending pass would answer "worst rate, and the longest-dated one too", which nobody asked for.
// Same reasoning as the Hedge Matrix's leg/card tie-breakers.
//
// ⚠ A comparator here NEVER returns 0 by falling off the end: Array.sort is not guaranteed stable on
// ES5 engines, and "the same inputs give the same order every run" is an acceptance criterion, not a
// nicety. The incoming index is the last word.
//
// A cell with NO rate (same-day, O49) is expected to have been dropped before this point. If one
// arrives anyway it sorts LAST in either direction rather than being interleaved on a guess - the
// same rule the Hedge Matrix applies to an unpriced package.

export function sortedByOrionKeys<T>(
    items: T[],
    keys: OrionSortKeys,
    typeOf: (item: T) => string,
    rateOf: (item: T) => number | undefined,
    daysOutOf: (item: T) => number
): T[] {

    const list = items || [];
    const decorated: { item: T; index: number; type: string; rate: number | undefined; daysOut: number }[] = [];

    for (let ix = 0; ix < list.length; ix++) {
        decorated.push({
            item: list[ix],
            index: ix,
            type: (typeOf(list[ix]) || '').toUpperCase(),
            rate: rateOf(list[ix]),
            daysOut: daysOutOf(list[ix])
        });
    }

    decorated.sort((a, b) => {

        if (keys.type !== 'none' && a.type !== b.type) {
            const ascending = a.type < b.type ? -1 : 1;
            return keys.type === 'asc' ? ascending : -ascending;
        }

        if (keys.price !== 'none') {
            if (a.rate === undefined && b.rate === undefined) {
                return a.index - b.index;
            }
            if (a.rate === undefined) {
                return 1;               // rate-less last, in EITHER direction
            }
            if (b.rate === undefined) {
                return -1;
            }
            if (a.rate !== b.rate) {
                return keys.price === 'desc'
                    ? (b.rate > a.rate ? 1 : -1)
                    : (a.rate > b.rate ? 1 : -1);
            }
            if (a.daysOut !== b.daysOut) {
                return a.daysOut - b.daysOut;   // the shorter expiration wins an exact tie
            }
        }

        return a.index - b.index;       // stability, explicitly - never left to the engine's sort
    });

    const out: T[] = [];
    for (let ix = 0; ix < decorated.length; ix++) {
        out.push(decorated[ix].item);
    }
    return out;
}
